import { CreateJoinMeetingSection } from '@/components/CreateJoinMeetingSection.tsx';
import Footer from '@/components/Footer';
import { LearnMorePanel } from '@/components/LearnMorePanel';
import { MainPageCarousel } from '@/components/MainPageCarousel.tsx';
import { Separator } from '@/components/ui/separator';
import { LoadingSpinner } from '@/components/ui/spinner';
import UpcomingEvents from '@/components/UpcomingEvents';
import { authService } from '@/service/AuthService';
import { calendarService } from '@/service/CalendarService';
import { CalendarEvent } from 'intermind';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Header from '../components/Header';

const isGoogleCalendarEnabled = import.meta.env.VITE_APP_GOOGLE_CALENDAR_ENABLED === 'true';

const HomePage = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false);

  useEffect(() => {
    toast.dismiss();
  }, []);

  const fetchUpcomingEvents = useCallback(async (): Promise<void> => {
    try {
      const eventsData = await calendarService.listUpcomingEvents();
      setEvents(eventsData || []);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const isAuthorized = await authService.isAuthorized();
        setIsAuthorized(isAuthorized);
        if (!isAuthorized) {
          return;
        }

        if (isGoogleCalendarEnabled) {
          const userInfo = await authService.getUserInfo();
          const isGoogleCalendarConnected = userInfo?.isGoogleCalendarConnected || false;
          setIsGoogleCalendarConnected(isGoogleCalendarConnected);

          if (isGoogleCalendarConnected) {
            await fetchUpcomingEvents();
          }
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchUpcomingEvents]);

  const handleConnectCalendar = async () => {
    const authUrl = await calendarService.getGoogleCalendarAuthUrl();
    if (authUrl) {
      window.location.href = authUrl;
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />
      <main className="w-full overflow-y-auto pt-16">
        <div className="flex flex-col gap-8 justify-center items-center my-8 px-4">
          {/* Hero Section */}
          <div className="flex items-end justify-center w-full max-w-xl">
            <div className="flex flex-col gap-6 w-full min-w-0">
              <h1 className="text-3xl md:text-5xl break-words text-center">{t('homepage.title')}</h1>
              <h2 className="text-lg text-muted-foreground break-words text-center">{t('homepage.subtitle')}</h2>
              <CreateJoinMeetingSection />
              <Separator className="hidden md:block portrait:block mt-2" />
            </div>
          </div>

          {/* Calendar / Carousel Section (shown on large or portrait screens) */}
          <div className="hidden md:flex portrait:flex flex-col gap-8 items-center justify-start w-full min-w-0 max-w-xl">
            <div className="w-full flex flex-col items-center gap-6 min-w-0">
              {isLoading ? (
                <LoadingSpinner size={12} className="text-indigo-600" />
              ) : !isAuthorized || !isGoogleCalendarConnected ? (
                <MainPageCarousel
                  isGoogleCalendarEnabled={isGoogleCalendarEnabled}
                  onConnectCalendar={handleConnectCalendar}
                />
              ) : events?.length > 0 ? (
                <UpcomingEvents events={events} />
              ) : (
                <div className="w-full flex flex-col items-center justify-center gap-2">
                  <div className="text-center">{t('homepage.upcomingEvents.noEvents')}</div>
                  {!isGoogleCalendarConnected && (
                    <div className="text-xs">
                      <button
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          handleConnectCalendar().catch((error) => {
                            console.error('Failed to connect calendar:', error);
                          });
                        }}
                        className="text-indigo-600 hover:underline transition-colors duration-200"
                      >
                        {t('homepage.upcomingEvents.connectCalendar')}
                      </button>{' '}
                      {t('homepage.upcomingEvents.connectCalendarDescription')}
                    </div>
                  )}
                </div>
              )}
            </div>
            <LearnMorePanel className="max-w-xl" id="learn-more-panel" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
